import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface AreaLink {
  label: string;
  to: string;
  search?: Record<string, string>;
  /** Die Seite selbst: nicht klickbar, für Vorleser als aktuelle Seite markiert. */
  current?: boolean;
}

export interface AreaSwitchItem extends AreaLink {
  active: boolean;
  /** `error`: die Anzahl konnte nicht geladen werden. */
  count?: number | 'error';
}

/** Was der Bereichskopf einer Seite zeigt: Pfad, Bereichsumschalter und Reiter der Seite. */
export interface AreaHeaderModel {
  crumbs: AreaLink[];
  switcher: AreaSwitchItem[];
  tabs: AreaSwitchItem[];
}

interface AreaHeaderContextValue extends AreaHeaderModel {
  /** Ein Seitenkopf meldet sich an; solange keiner da ist, zeigt das Gerüst eine schmale Kopfleiste. */
  register: () => () => void;
  headers: number;
}

const EMPTY: AreaHeaderModel = { crumbs: [], switcher: [], tabs: [] };
const AreaHeaderContext = createContext<AreaHeaderContextValue | null>(null);

export function AreaHeaderProvider({
  model,
  children,
}: {
  model: AreaHeaderModel;
  children: ReactNode;
}) {
  const [headers, setHeaders] = useState(0);
  const register = useCallback(() => {
    setHeaders((count) => count + 1);
    return () => setHeaders((count) => count - 1);
  }, []);
  const value = useMemo(() => ({ ...model, register, headers }), [model, register, headers]);
  return <AreaHeaderContext.Provider value={value}>{children}</AreaHeaderContext.Provider>;
}

/** Bereichskopf-Daten; außerhalb des Gerüsts (Bühnen, Tests) leer. */
export function useAreaHeader(): AreaHeaderModel & { headers: number } {
  const value = useContext(AreaHeaderContext);
  return value ?? { ...EMPTY, headers: 0 };
}

/**
 * Meldet einen vollständigen Seitenkopf an. `useLayoutEffect`, damit die schmale Ersatzleiste des
 * Gerüsts im selben Bild verschwindet und nicht kurz aufblitzt.
 */
export function useRegisterAreaHeader(): void {
  const register = useContext(AreaHeaderContext)?.register;
  useLayoutEffect(() => register?.(), [register]);
}
